import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * Paginated/infinite list.
 * VELUNE PERFORMANCE V2:
 * - defers speculative adjacent-page requests until the browser is idle;
 * - cleans filter observers/timeouts;
 * - guards optional aspect-ratio helper;
 * - preserves viewport-driven infinite scrolling.
 */
export default class PaginatedList extends Component {
  /** @type {Map<number, string>} */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /** @type {MutationObserver | null} */
  #filterObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #filterObserverTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    }

    this.#observeViewMore();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    // Adjacent-page prefetch is useful, but it should not compete with LCP,
    // image decoding, fonts, or the first interaction after navigation.
    requestIdleCallback(() => {
      if (!this.isConnected) return;
      this.#fetchPage('next');
      this.#fetchPage('previous');
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = undefined;
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.#cleanupFilterObserver();
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) await viewTransition.current;
          if (!this.isConnected) return;

          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const currentRefs = this.refs;
            if (entry.target === currentRefs.viewMorePrevious) {
              this.#renderPreviousPage();
            } else if (entry.target === currentRefs.viewMoreNext) {
              this.#renderNextPage();
            }
          }
        },
        { rootMargin: '100px' }
      );
    }

    if (viewMorePrevious) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext) this.infinityScrollObserver.observe(viewMoreNext);
  }

  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const lastPage = this.refs.grid?.dataset.lastPage;
    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;
    return true;
  }

  async #fetchPage(type) {
    const page = this.#getPage(type);

    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      console.error('Unable to prefetch paginated section:', error);
    } finally {
      resolvePromise();
    }
  }

  async #fetchSpecificPage(pageNumber, url = undefined) {
    if (this.pages.has(pageNumber)) return;

    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    if (!this.isConnected) return;
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    let elements = this.#getGridForPage(nextPage.page);

    if (!elements) {
      const promise = new Promise((resolve) => {
        this.#resolveNextPagePromise = resolve;
      });
      this.#fetchPage('next');
      await promise;
      if (!this.isConnected) return;
      elements = this.#getGridForPage(nextPage.page);
      if (!elements) return;
    }

    grid.append(...elements);
    this.#aspectRatioHelper?.processNewElements();
    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      if (this.isConnected) this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let elements = this.#getGridForPage(previousPage.page);

    if (!elements) {
      const promise = new Promise((resolve) => {
        this.#resolvePreviousPagePromise = resolve;
      });
      this.#fetchPage('previous');
      await promise;
      if (!this.isConnected) return;
      elements = this.#getGridForPage(previousPage.page);
      if (!elements) return;
    }

    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    grid.prepend(...elements);
    this.#aspectRatioHelper?.processNewElements();
    history.pushState('', '', previousPage.url.toString());

    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: scrollTop + (newHeight - oldHeight), behavior: 'instant' });
    }

    requestIdleCallback(() => {
      if (this.isConnected) this.#fetchPage('previous');
    });
  }

  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';
    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];
    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return { page, url };
  }

  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #cleanupFilterObserver() {
    this.#filterObserver?.disconnect();
    this.#filterObserver = null;

    if (this.#filterObserverTimeout) {
      clearTimeout(this.#filterObserverTimeout);
      this.#filterObserverTimeout = null;
    }
  }

  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();
    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    this.#cleanupFilterObserver();

    const currentLastPage = this.refs.grid?.dataset.lastPage;
    const { grid } = this.refs;
    if (!grid) return;

    this.#filterObserver = new MutationObserver(() => {
      const newLastPage = this.refs.grid?.dataset.lastPage;
      if (newLastPage === currentLastPage && this.refs.grid === grid) return;

      this.#cleanupFilterObserver();
      if (!this.isConnected) return;

      this.#observeViewMore();
      requestIdleCallback(() => {
        if (this.isConnected) this.#fetchPage('next');
      });
    });

    this.#filterObserver.observe(grid, {
      attributes: true,
      attributeFilter: ['data-last-page'],
      childList: true,
    });

    this.#filterObserverTimeout = setTimeout(() => {
      this.#cleanupFilterObserver();
    }, 3000);
  };
}
